import { Component, Input } from '@angular/core';
import {
	Device,
	DeviceStatus,
	DeviceGroupStatus,
	DeviceGroupDeviceStatus,
	DeviceDisplayStatus,
	DEVICE_GENERATION_3,
	isPdqDevice,
	isSentriVaultDevice,
} from 'src/app/models/data/device';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { NgClass } from '@angular/common';

const SENTRIGUARD_NAME_FRAGMENT = 'sentriguard';
const SENTURIAN_NAME_FRAGMENT = 'senturian';
const SENTRIVAULT_NAME_FRAGMENT = 'sentrivault';

/**
 * Device shape accepted by {@link DeviceStatusComponent}. Every field is optional so
 * list rows, detail payloads and group rows can all be passed without adaptation.
 */
export interface StatusDevice extends Partial<Device> {
	deviceGroupDeviceStatus?: DeviceGroupDeviceStatus;
}

/**
 * Removal timestamps that drive the Ready for Removal chip, regardless of which
 * payload the caller read them from.
 */
export interface StatusInstallationMetadata {
	utcRemovalRequested?: string;
	utcUninstallDate?: string;
}

/**
 * Renders the device status chip, plus a second red "Ready for Removal" chip when the
 * device's current installation has been marked for removal and not yet uninstalled.
 *
 * The removal chip is restricted to supported lockbox hardware (SentriGuard Gen3/Gen4)
 * and is never gated on edit permissions, so viewers and editors see the same status.
 */
@Component({
	selector: 'app-device-status',
	templateUrl: './device-status.component.html',
	styleUrls: ['./device-status.component.scss'],
	imports: [MatChipSet, MatChip, NgClass],
})
export class DeviceStatusComponent {
	/** Device whose status is displayed. */
	@Input() device!: StatusDevice;

	/** Current installation, when the caller already has it resolved. */
	@Input() installation?: StatusInstallationMetadata;

	/** Removal timestamps for payloads that carry no full installation record. */
	@Input() installationMetadata?: StatusInstallationMetadata;

	getStatusName(status: DeviceStatus | DeviceGroupStatus): string {
		switch (status) {
			case 'active':
				return $localize`:@@deviceGroupStatusActive:Active`;
			case 'upcoming':
				return $localize`:@@deviceGroupStatusUpcoming:Upcoming`;
			case 'expired':
				return $localize`:@@deviceGroupStatusInactive:Inactive`;

			case 'available':
				return $localize`:@@deviceStatusAvailable:Available`;
			case 'inService':
				return $localize`:@@deviceStatusInService:In Service`;
			case 'inactive':
				return $localize`:@@deviceStatusInactive:Inactive`;
			case 'unknown':
				return $localize`:@@deviceStatusUnknown:Unknown`;
		}
		return $localize`:@@deviceStatusUnknown:Unknown`;
	}

	getDeviceStatus(device: StatusDevice): DeviceDisplayStatus {
		return device.displayStatus ?? device.status ?? 'unknown';
	}

	getChipStyling(status: DeviceStatus | DeviceGroupStatus): string {
		switch (status) {
			case 'upcoming':
				return 'status-chip-upcoming';
			case 'expired':
				return 'status-chip-inactive';
			case undefined:
				return 'status-chip-active';
		}
		return 'status-chip-active';
	}

	/**
	 * True when the device is an in-service supported lockbox whose current installation
	 * has a removal request and no uninstall date.
	 */
	isReadyForRemoval(): boolean {
		if (this.getDeviceStatus(this.device) !== 'inService') {
			return false;
		}
		if (!this.isSupportedLockbox(this.device)) {
			return false;
		}
		return !!this.getUtcRemovalRequested() && !this.getUtcUninstallDate();
	}

	private getUtcRemovalRequested(): string | undefined {
		return (
			this.installation?.utcRemovalRequested ??
			this.installationMetadata?.utcRemovalRequested ??
			this.device?.activeDeviceAssignment?.activeInstallation
				?.utcRemovalRequested
		);
	}

	private getUtcUninstallDate(): string | undefined {
		return (
			this.installation?.utcUninstallDate ??
			this.installationMetadata?.utcUninstallDate ??
			this.device?.activeDeviceAssignment?.activeInstallation?.utcUninstallDate
		);
	}

	/**
	 * Single source of truth for which hardware may show the removal chip: SentriGuard
	 * lockboxes (Gen3 and Gen4), never Senturian/PDQ keys or SentriVault.
	 */
	private isSupportedLockbox(device: StatusDevice): boolean {
		// SentriVault impersonates SentriGuard naming, so exclusions run before the allow-list.
		const identity = device as Device;
		if (
			isSentriVaultDevice(identity) ||
			isPdqDevice(identity) ||
			this.matchesProductName(device, SENTURIAN_NAME_FRAGMENT) ||
			this.matchesProductName(device, SENTRIVAULT_NAME_FRAGMENT)
		) {
			return false;
		}
		return (
			device.deviceGeneration?.toLowerCase() === DEVICE_GENERATION_3 ||
			this.matchesProductName(device, SENTRIGUARD_NAME_FRAGMENT)
		);
	}

	private matchesProductName(device: StatusDevice, fragment: string): boolean {
		return (
			(device.deviceType?.toLowerCase() ?? '').includes(fragment) ||
			(device.deviceMarketingName?.toLowerCase() ?? '').includes(fragment)
		);
	}
}
